/**
 * Runtime wiring for the whole web app.
 *
 * The REAL backend is the default. `VITE_USE_MOCK=true` puts every feature back
 * on its fixtures so the UI can still be demoed with no server running — the
 * mock path is kept alive on purpose, it is simply no longer the default.
 */

/**
 * `/v1`, NOT `/api/v1` — and this one matters for authentication.
 *
 * The refresh token is an httpOnly cookie scoped to `Path=/v1/auth`, and a
 * browser matches `Path` against the URL IT requested, not the one the upstream
 * server sees. Served behind a rewriting `/api` prefix the cookie would be set
 * and then never sent back, so every page reload would sign the user out.
 * vite.config.ts proxies `/v1` verbatim; production nginx must do the same.
 */
const DEFAULT_API_BASE = '/v1';
const DEFAULT_WS_PATH = '/ws';

/** `true` only for the literal string `"true"` — anything else means live. */
export const USE_MOCK: boolean = import.meta.env.VITE_USE_MOCK === 'true';

/** REST base with no trailing slash. */
export const API_BASE_URL: string = (import.meta.env.VITE_API_URL ?? DEFAULT_API_BASE).replace(
  /\/+$/,
  '',
);

export const CLIENT_VERSION: string = import.meta.env.VITE_CLIENT_VERSION ?? 'web/1.0.0';

/**
 * Gateway URL. Relative by default so the Vite dev proxy (and nginx in prod)
 * decides where :3022 actually lives — no host is ever hard-coded in a bundle.
 */
export function gatewayUrl(): string {
  const configured = import.meta.env.VITE_WS_URL;
  if (configured !== undefined && configured !== '') return configured;
  if (typeof window === 'undefined') return `ws://127.0.0.1:3022${DEFAULT_WS_PATH}`;
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}${DEFAULT_WS_PATH}`;
}
